import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { Text } from 'office-ui-fabric-react';
import cn from 'classnames';
import { semantic } from '../../tokens';
/**
 * Vendor mapping — replaced wholesale at migration (`adapter.v9.tsx` etc).
 *
 * `preset` targets pixel-identical output to the 14 wrappers it replaces, so
 * `lineHeight` is only set where the original wrapper set one explicitly
 * (`body2`, `title2`) — every other preset stays unset on purpose, even
 * though real v9 line-heights are known (see `SIZE_STYLE`). `size` mode has
 * no such parity constraint, so it uses real v9 values from the start.
 */ var PRESET_STYLE = {
    caption2: {
        fontSize: 10,
        fontWeight: 400
    },
    caption1: {
        fontSize: 12,
        fontWeight: 400
    },
    caption1Strong: {
        fontSize: 12,
        fontWeight: 600
    },
    body1: {
        fontSize: 14,
        fontWeight: 400
    },
    body1Strong: {
        fontSize: 14,
        fontWeight: 600
    },
    body1Stronger: {
        fontSize: 14,
        fontWeight: 700
    },
    body2: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '22px'
    },
    subtitle2: {
        fontSize: 16,
        fontWeight: 600
    },
    subtitle2Stronger: {
        fontSize: 16,
        fontWeight: 700
    },
    subtitle1: {
        fontSize: 20,
        fontWeight: 600
    },
    title3: {
        fontSize: 24,
        fontWeight: 600
    },
    title2: {
        fontSize: 28,
        fontWeight: 600,
        lineHeight: '36px'
    },
    title1: {
        fontSize: 32,
        fontWeight: 600
    },
    largeTitle: {
        fontSize: 40,
        fontWeight: 600
    }
};
/** Real v9 size ramp (`@fluentui/tokens`'s `fonts.ts`). No `fontWeight` here — size/weight are independent axes, unlike `preset`. */ var SIZE_STYLE = {
    100: {
        fontSize: 10,
        lineHeight: '14px'
    },
    200: {
        fontSize: 12,
        lineHeight: '16px'
    },
    300: {
        fontSize: 14,
        lineHeight: '20px'
    },
    400: {
        fontSize: 16,
        lineHeight: '22px'
    },
    500: {
        fontSize: 20,
        lineHeight: '28px'
    },
    600: {
        fontSize: 24,
        lineHeight: '32px'
    },
    700: {
        fontSize: 28,
        lineHeight: '36px'
    },
    800: {
        fontSize: 32,
        lineHeight: '40px'
    },
    900: {
        fontSize: 40,
        lineHeight: '52px'
    },
    1000: {
        fontSize: 68,
        lineHeight: '92px'
    }
};
/** Matches v9's real `Text.types.ts` weight vocabulary. */ var WEIGHT_VALUE = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700
};
/** Always `semantic.*`, never a raw hex — so migration only touches `tokens/semantic.ts`. */ var COLOR_VALUE = {
    default: semantic.colorNeutralForeground1Body,
    secondary: semantic.colorNeutralForeground2,
    tertiary: semantic.colorNeutralForeground3,
    muted: semantic.colorNeutralForegroundMuted,
    brand: semantic.colorBrandForeground1,
    onBrand: semantic.colorNeutralForegroundOnBrand,
    danger: semantic.colorPaletteRedForegroundText,
    success: semantic.colorPaletteGreenForegroundText,
    warning: semantic.colorPaletteYellowForegroundText
};
/**
 * `ai` is a gradient, which `color` cannot hold: it is painted as a background clipped to the
 * glyphs with the fill made transparent (the same trick `TFButton.less` uses for `ai-outline`).
 * `color` stays the solid brand foreground so a `currentColor` SVG inside still has a value.
 */ var colorStyle = function colorStyle(color) {
    return color === 'ai' ? {
        color: semantic.colorBrandForeground1,
        background: semantic.colorAiForeground,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent'
    } : {
        color: COLOR_VALUE[color]
    };
};
/** Combines underline+strikethrough into one `text-decoration` instead of one overriding the other. */ var textDecoration = function textDecoration(underline, strikethrough) {
    var lines = [
        underline && 'underline',
        strikethrough && 'line-through'
    ].filter(Boolean);
    return lines.length ? lines.join(' ') : undefined;
};
export var renderTFText = function renderTFText(_0) {
    var preset = _0.preset, size = _0.size, weight = _0.weight, children = _0.children, as = _0.as, color = _0.color, _0_block = _0.block, block = _0_block === void 0 ? true : _0_block, _0_wrap = _0.wrap, wrap = _0_wrap === void 0 ? true : _0_wrap, truncate = _0.truncate, align = _0.align, italic = _0.italic, underline = _0.underline, strikethrough = _0.strikethrough, onClick = _0.onClick, id = _0.id, className = _0.className, title = _0.title, style = _0.style, rest = _object_without_properties(_0, [
        "preset",
        "size",
        "weight",
        "children",
        "as",
        "color",
        "block",
        "wrap",
        "truncate",
        "align",
        "italic",
        "underline",
        "strikethrough",
        "onClick",
        "id",
        "className",
        "title",
        "style"
    ]);
    if (process.env.NODE_ENV !== 'production') {
        // No keyboard affordance for a click on plain text — use TFButton (link/transparent) instead.
        if (onClick) {
            console.warn("TFText: `onClick` makes this element clickable by mouse only — it has no keyboard affordance. Prefer TFButton (variant='link' or 'transparent') for a real interactive element.");
        }
        if (truncate && !title && !rest['aria-label']) {
            console.warn('TFText: `truncate` hides overflow text visually — pass `title` (or `aria-label`) with the full text so it can still be read on hover.');
        }
    }
    // Dispatch on size OR weight being set — weight can be passed alone (v9's own Text allows it).
    var isSizeMode = size !== undefined || weight !== undefined;
    var typography = isSizeMode ? _object_spread_props(_object_spread({}, SIZE_STYLE[size !== null && size !== void 0 ? size : 300]), {
        fontWeight: WEIGHT_VALUE[weight !== null && weight !== void 0 ? weight : 'regular']
    }) : PRESET_STYLE[preset !== null && preset !== void 0 ? preset : 'body1'];
    var modeClassName = isSizeMode ? "tf-text--size-".concat(size !== null && size !== void 0 ? size : 300) : "tf-text--".concat(preset !== null && preset !== void 0 ? preset : 'body1');
    return /*#__PURE__*/ _jsx(Text, _object_spread_props(_object_spread({}, rest), {
        id: id,
        title: title,
        as: as,
        block: block,
        onClick: onClick,
        className: cn('tf-text', modeClassName, className),
        style: _object_spread(_object_spread_props(_object_spread({}, typography, colorStyle(color !== null && color !== void 0 ? color : 'default')), {
            textAlign: align,
            fontStyle: italic ? 'italic' : undefined,
            textDecoration: textDecoration(underline, strikethrough),
            // wrap/truncate are independent booleans (matching v9), not Fabric's native `nowrap`.
            whiteSpace: truncate || !wrap ? 'nowrap' : undefined,
            overflow: truncate ? 'hidden' : undefined,
            textOverflow: truncate ? 'ellipsis' : undefined
        }), style),
        children: children
    }));
};
